/**
 * SourcesPanel - Left Panel (VAULT FILES LIST)
 *
 * COPIED DIRECTLY from index.jsx (lines 3059-3927)
 * This is the EXACT working code, not a reinvention.
 *
 * @module components/panels/SourcesPanel
 */

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';

import {
  FileText,
  Search,
  Plus,
  Upload,
  ChevronRight,
  PanelLeftClose,
  AlertCircle,
  Loader2,
  CheckCircle2,
  MoreVertical,
  Edit3,
  Trash2,
  RotateCcw,
  X,
  Zap,
} from 'lucide-react';

import { formatBytes } from '../../../../utils/formatters';
import { getFileTypeTooltip } from '@/pages/intelligence-studio/utils/fileUtils';
import { normalizeUniversalError, isUIUniversalError } from '@/pages/intelligence-studio/utils/universalError';

// ==========================================
// LEFT: SOURCES PANEL (VAULT FILES LIST)
// ==========================================

const SourcesPanel = ({
  notebooks,
  selectedNotebook,
  onSelect,
  onDelete,
  onNewNotebook,
  collapsed,
  onToggleCollapse,
  isLoading,
  onFileSelect,
  selectedFileId,
  onUploadFile,
  onRemoveDuplicates,
  onRetryFile,
  onRemoveFile,
  onDeleteFile,
  onRenameFile,
  onReuploadFile,
  onReprocessFile,
  width = 280,
}) => {
  const STATE_RESOLVER_ENABLED = process.env.REACT_APP_IS_STATE_RESOLVER !== 'false';
  const [search, setSearch] = useState('');
  const [isRemovingDupes, setIsRemovingDupes] = useState(false);
  const [isReindexingAll, setIsReindexingAll] = useState(false);
  const autoReindexSignatureRef = useRef('');
  const fileInputRef = useRef(null);

  // When inside a notebook, show its FILES. Otherwise show notebooks list.
  const insideNotebook = !!selectedNotebook;
  const files = selectedNotebook?.files || [];

  // ✅ DEBUG: Log files for troubleshooting
  console.log(`📁 [SourcesPanel] insideNotebook: ${insideNotebook}, files: ${files.length}`);
  if (files.length > 0 && files.length <= 5) {
    files.forEach((f, i) => console.log(`   File ${i + 1}: ${f.originalName || f.name}`));
  }

  const filteredFiles = files.filter(f =>
    (f.originalName || f.name || '').toLowerCase().includes(search.toLowerCase())
  );

  // ✅ PHASE 5: Progressive rendering for large vaults (prevents left panel freezes)
  const [visibleFilesCount, setVisibleFilesCount] = useState(200);
  useEffect(() => {
    if (!insideNotebook) return;
    const total = filteredFiles.length;
    const initial = total > 400 ? 80 : 200;
    setVisibleFilesCount(Math.min(total, initial));

    if (total <= initial) return;

    let cancelled = false;
    let idleId = null;
    let tId = null;

    const step = () => {
      if (cancelled) return;
      setVisibleFilesCount(prev => {
        const next = Math.min(total, prev + 60);
        return next;
      });
    };

    const schedule = () => {
      if (cancelled) return;
      if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
        idleId = window.requestIdleCallback(() => {
          step();
          schedule();
        });
      } else {
        tId = setTimeout(() => {
          step();
          schedule();
        }, 50);
      }
    };

    schedule();

    return () => {
      cancelled = true;
      try {
        if (idleId && typeof window !== 'undefined' && typeof window.cancelIdleCallback === 'function') {
          window.cancelIdleCallback(idleId);
        }
      } catch {}
      if (tId) clearTimeout(tId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insideNotebook, selectedNotebook?.id, search, filteredFiles.length]);

  const displayFiles =
    insideNotebook && filteredFiles.length > 250
      ? filteredFiles.slice(0, Math.max(1, visibleFilesCount))
      : filteredFiles;

  const vaultStateData = STATE_RESOLVER_ENABLED
    ? (selectedNotebook?._vaultState?.vaultState ? selectedNotebook._vaultState : selectedNotebook?._vaultState?.data)
    : null;
  const vaultPhase = String(vaultStateData?.vaultState || '').toUpperCase();
  const vaultProcessingCount = Number(vaultStateData?.files?.processing || 0) || 0;
  const vaultReady = vaultPhase === 'READY';
  const hasActiveIngestion = vaultPhase === 'INGESTING' || vaultPhase === 'INIT' || vaultProcessingCount > 0;
  const vaultFileMap = useMemo(() => {
    const details = Array.isArray(vaultStateData?.fileDetails) ? vaultStateData.fileDetails : null;
    if (!details) return null;
    return new Map(details.map(fd => [String(fd.fileId), fd]));
  }, [vaultStateData]);

  // ✅ ENTERPRISE: Detect files that are "Ready" but NOT indexed (0 chunks / 0 vectors)
  const unindexedFiles = useMemo(() => {
    if (vaultPhase === 'INGESTING' || vaultPhase === 'INIT') {
      return [];
    }

    return (files || []).filter(f => {
      if (vaultFileMap) {
        const fid = f.fileId || f.id;
        const details = fid ? vaultFileMap.get(String(fid)) : null;
        if (!details) return false;
        if (details.state !== 'READY') return false;
        if (typeof details.indexReady === 'boolean') {
          return details.indexReady === false;
        }
        const effective = Number(details.effectiveChunkCount || 0) || 0;
        const qdrant = details.qdrantCount === null ? null : (Number(details.qdrantCount || 0) || 0);
        if (qdrant === null) return effective === 0;
        return effective === 0 || qdrant === 0;
      }

      const status = String(f.status || '').toLowerCase();
      const isReady = f.isReady === true || status === 'ready' || status === 'ready_with_warnings';
      if (!isReady) return false;
      if (['duplicate', 'failed', 'blocked_low_quality'].includes(status)) return false;

      const lifecycle = f.lifecycleSnapshot || null;
      if (lifecycle?.queryability?.queryable === true) return false;

      const chunks = Number(f.chunkCount || 0) || (Array.isArray(f.chunks) ? f.chunks.length : 0);
      const vectors = Number(f.embeddingCount || 0) || Number(f.vectorCount || 0) || 0;
      const indexReady = f.vectorIndexReady !== false && f.embeddingQueryReady !== false;
      if (indexReady && chunks > 0 && vectors > 0) return false;

      // If either is missing, retrieval will be degraded (vector search needs embeddings)
      return chunks === 0 || vectors === 0;
    });
  }, [files, vaultFileMap, vaultPhase]);

  const hasUnindexed = insideNotebook && unindexedFiles.length > 0;
  const showReindexBanner =
    insideNotebook &&
    (typeof vaultStateData?.reindexBanner === 'boolean'
      ? vaultStateData.reindexBanner
      : vaultReady && !hasActiveIngestion && hasUnindexed);

  const ghostCount = Number(vaultStateData?.ghostCount || 0) || 0;
  const lastSanitizedAt = vaultStateData?.lastSanitizedAt || null;
  const showGhostHealedBanner =
    insideNotebook &&
    ghostCount === 0 &&
    Boolean(lastSanitizedAt) &&
    Date.now() - new Date(lastSanitizedAt).getTime() < 5 * 60 * 1000;

  const handleReindexAll = useCallback(async () => {
    if (!onReprocessFile || !selectedNotebook?.id) return;
    if (isReindexingAll) return;
    setIsReindexingAll(true);
    try {
      // Sequential to avoid hammering the backend + keeps UX predictable
      for (const f of unindexedFiles) {
        // eslint-disable-next-line no-await-in-loop
        await onReprocessFile(f, 'Re-index: missing search index');
      }
    } finally {
      setIsReindexingAll(false);
    }
  }, [isReindexingAll, onReprocessFile, selectedNotebook?.id, unindexedFiles]);

  // Auto-trigger re-index when files are in "ready but not indexed" state.
  useEffect(() => {
    if (!insideNotebook || !selectedNotebook?.id || !hasUnindexed || !onReprocessFile) {
      if (!hasUnindexed) autoReindexSignatureRef.current = '';
      return;
    }
    if (isReindexingAll) return;

    const fileIds = unindexedFiles
      .map(f => String(f.fileId || f.id || ''))
      .filter(Boolean)
      .sort();
    if (fileIds.length === 0) return;

    const signature = `${selectedNotebook.id}:${fileIds.join(',')}`;
    if (autoReindexSignatureRef.current === signature) return;

    autoReindexSignatureRef.current = signature;
    console.log(`[IS] Auto-reindexing ${unindexedFiles.length} files`, {
      notebookId: selectedNotebook.id,
      fileIds,
    });
    void handleReindexAll();
  }, [
    handleReindexAll,
    hasUnindexed,
    insideNotebook,
    isReindexingAll,
    onReprocessFile,
    selectedNotebook?.id,
    unindexedFiles,
  ]);

  const filteredNotebooks = notebooks.filter(n =>
    n.title?.toLowerCase().includes(search.toLowerCase())
  );

  // ✅ ENTERPRISE: Detect duplicates by CONTENT HASH (not filename)
  // This aligns UI with backend dedup logic (hash-based).
  const duplicatesByHash = useMemo(() => {
    const hashCounts = {};
    files.forEach(f => {
      const h = f.fileHash || f.hash || null;
      if (!h) return;
      hashCounts[h] = (hashCounts[h] || 0) + 1;
    });
    return Object.entries(hashCounts).filter(([, count]) => count > 1);
  }, [files]);

  const hasDuplicates = duplicatesByHash.length > 0;
  const totalDuplicates = duplicatesByHash.reduce((acc, [, count]) => acc + (count - 1), 0);

  // Handle remove duplicates
  const handleRemoveDuplicates = async () => {
    if (!selectedNotebook?.id || !onRemoveDuplicates) return;
    setIsRemovingDupes(true);
    try {
      await onRemoveDuplicates(selectedNotebook.id);
    } finally {
      setIsRemovingDupes(false);
    }
  };

  const handleFileUpload = async e => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length > 0 && selectedNotebook?.id && onUploadFile) {
      // ✅ ENTERPRISE UX FIX:
      // Start multiple uploads without forcing strictly sequential execution (which *looks* like single-select).
      // Concurrency is capped to avoid hammering the backend if users select many files at once.
      const MAX_PARALLEL_UPLOADS = 3;

      const queue = [...selectedFiles];
      const workers = new Array(Math.min(MAX_PARALLEL_UPLOADS, queue.length))
        .fill(null)
        .map(async () => {
          while (queue.length > 0) {
            const file = queue.shift();
            if (!file) continue;
            try {
              // eslint-disable-next-line no-await-in-loop
              await onUploadFile(selectedNotebook.id, file);
            } catch (err) {
              // Continue uploading the remaining files even if one fails
              console.warn(
                `⚠️ [SourcesPanel] Upload failed for "${file.name}":`,
                err?.message || err
              );
            }
          }
        });

      await Promise.all(workers);
    }
    e.target.value = '';
  };

  return (
    <div className="h-full flex flex-col bg-slate-50 dark:bg-neutral-800 overflow-hidden">
      {/* Header - h-10 (40px) for perfect alignment with all columns */}
      <div className="flex items-center justify-between px-3 h-10 border-b border-slate-200 dark:border-neutral-700">
        {!collapsed ? (
          <span className="text-xs font-medium text-slate-500 dark:text-neutral-400 uppercase tracking-wide flex items-center gap-2">
            {insideNotebook ? 'Sources' : 'Notebooks'}
            {insideNotebook && files.length > 0 && (
              <span className="px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 rounded-full text-[10px] font-bold">
                {files.length}
              </span>
            )}
          </span>
        ) : (
          <div className="flex-1" />
        )}
        <button
          onClick={onToggleCollapse}
          className="p-1.5 hover:bg-slate-200 dark:hover:bg-neutral-700 rounded-lg transition-colors"
          title={collapsed ? 'Expand' : 'Collapse'}
        >
          {collapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <PanelLeftClose className="h-4 w-4" />
          )}
        </button>
      </div>

      {!collapsed && (
        <>
          {/* Search */}
          <div className="p-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
              <input
                type="text"
                placeholder={insideNotebook ? 'Search sources...' : 'Search notebooks...'}
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 bg-white dark:bg-neutral-700 border border-slate-200 dark:border-neutral-600 rounded-lg text-sm placeholder:text-slate-400 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* ✅ Duplicate Files Warning */}
          {insideNotebook && hasDuplicates && (
            <div className="mx-2 mb-2 p-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
              <div className="flex items-center gap-2 text-xs text-amber-700 dark:text-amber-300">
                <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
                <span>
                  {totalDuplicates} duplicate file{totalDuplicates !== 1 ? 's' : ''} detected
                </span>
              </div>
              <button
                onClick={handleRemoveDuplicates}
                disabled={isRemovingDupes}
                className="mt-1.5 w-full text-xs px-2 py-1 bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50 flex items-center justify-center gap-1"
              >
                {isRemovingDupes ? (
                  <>
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Removing...
                  </>
                ) : (
                  <>🧹 Remove Duplicates</>
                )}
              </button>
            </div>
          )}

          {/* INSIDE NOTEBOOK: Show files list */}
          {insideNotebook ? (
            <>
              {/* ✅ ENTERPRISE: Indexing warning (user-friendly guidance + one-click fix) */}
              {showReindexBanner && (
                <div className="mx-2 mb-2 p-2 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg">
                  <div className="flex items-start gap-2 text-xs text-orange-700 dark:text-orange-300">
                    <AlertCircle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                    <div className="min-w-0">
                      <div className="font-medium">Some sources aren’t indexed yet</div>
                      <div className="text-[11px] opacity-90">
                        These files show “Ready” but aren’t fully indexed yet, so Q&amp;A may return
                        empty answers.
                        <span className="block">Indexing in progress…</span>
                      </div>
                    </div>
                  </div>
                  <div
                    className="mt-1.5 w-full text-xs px-2 py-1 bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-200 rounded flex items-center justify-center gap-1"
                    title="Auto re-indexing in progress"
                  >
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Indexing in progress…
                  </div>
                </div>
              )}
              {showGhostHealedBanner && (
                <div className="mx-2 mb-2 p-2 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg">
                  <div className="flex items-start gap-2 text-xs text-emerald-700 dark:text-emerald-300">
                    <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                    <div className="min-w-0">
                      <div className="font-medium">We automatically repaired an internal file state.</div>
                      <div className="text-[11px] opacity-90">No action needed.</div>
                    </div>
                  </div>
                </div>
              )}
              <div className="flex-1 overflow-y-auto p-2 space-y-1">
                {filteredFiles.length === 0 ? (
                  <div className="text-center py-8">
                    <FileText className="h-8 w-8 mx-auto text-slate-300 dark:text-neutral-600 mb-2" />
                    <p className="text-xs text-slate-400">No sources yet</p>
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="mt-2 text-xs text-amber-600 hover:underline"
                    >
                      Upload files
                    </button>
                  </div>
                ) : (
                  displayFiles.map((file, idx) => {
                    // Get original index (for source number) from the full files array
                    const originalIndex = files.findIndex(
                      f => (f.fileId || f.id) === (file.fileId || file.id)
                    );
                    const sourceNumber = originalIndex >= 0 ? originalIndex + 1 : idx + 1;

                    return (
                      <SourceFileItem
                        key={file.fileId || file.id || idx}
                        file={file}
                        vaultDetails={
                          vaultFileMap
                            ? vaultFileMap.get(String(file.fileId || file.id || '')) || null
                            : null
                        }
                        isSelected={selectedFileId === (file.fileId || file.id)}
                        onClick={() => onFileSelect?.({ ...file, sourceNumber })}
                        sourceNumber={sourceNumber}
                        onRetry={onRetryFile}
                        onRemove={onRemoveFile}
                        onDelete={onDeleteFile}
                        onRename={onRenameFile}
                        onReupload={onReuploadFile}
                        onReprocess={onReprocessFile}
                      />
                    );
                  })
                )}
                {insideNotebook && displayFiles.length < filteredFiles.length && (
                  <div className="py-2 text-center text-[11px] text-slate-400 dark:text-neutral-500">
                    Loading more sources… ({displayFiles.length}/{filteredFiles.length})
                  </div>
                )}
              </div>

              {/* Upload button */}
              <div className="p-2 border-t border-slate-200 dark:border-neutral-700">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full flex items-center gap-2 p-2 text-sm text-slate-600 dark:text-neutral-300 hover:bg-slate-100 dark:hover:bg-neutral-700 rounded-lg transition-colors"
                >
                  <Upload className="h-4 w-4" />
                  <span>Add Source</span>
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={handleFileUpload}
                  className="hidden"
                  accept=".pdf,.docx,.xls,.xlsx,.txt,.csv,.jpg,.jpeg,.png,.gif,.webp,.tiff,.tif"
                />
              </div>
            </>
          ) : (
            <>
              {/* HOME: Show notebooks list */}
              <div className="flex-1 overflow-y-auto p-2 space-y-1">
                {isLoading ? (
                  <div className="flex justify-center py-4">
                    <Loader2 className="h-5 w-5 animate-spin text-amber-600" />
                  </div>
                ) : filteredNotebooks.length === 0 ? (
                  <p className="text-xs text-slate-400 text-center py-4">No notebooks</p>
                ) : (
                  filteredNotebooks.map(notebook => (
                    <NotebookListItem
                      key={notebook.id}
                      notebook={notebook}
                      isSelected={selectedNotebook?.id === notebook.id}
                      onSelect={() => onSelect(notebook)}
                      onDelete={() => onDelete(notebook)}
                    />
                  ))
                )}
              </div>

              {/* New studio button */}
              <div className="p-2 border-t border-slate-200 dark:border-neutral-700">
                <button
                  onClick={onNewNotebook}
                  className="w-full flex items-center gap-2 p-2 text-sm text-slate-600 dark:text-neutral-300 hover:bg-slate-100 dark:hover:bg-neutral-700 rounded-lg transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  <span>New Studio</span>
                </button>
              </div>
            </>
          )}
        </>
      )}

      {collapsed && (
        <div className="flex-1 flex flex-col">
          {/* Top action row with border to align with middle panel */}
          <div className="flex justify-center py-2 border-b border-slate-200 dark:border-neutral-700">
            {insideNotebook ? (
              <button
                onClick={() => fileInputRef.current?.click()}
                className="p-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors"
                title="Add Source"
              >
                <Plus className="h-4 w-4" />
              </button>
            ) : (
              <button
                onClick={onNewNotebook}
                className="p-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors"
                title="New Studio"
              >
                <Plus className="h-4 w-4" />
              </button>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileUpload}
            className="hidden"
          />

          {/* Files/Notebooks list */}
          <div className="flex-1 flex flex-col items-center pt-2 space-y-2 overflow-y-auto">
            {insideNotebook ? (
              <>
                {files.slice(0, 8).map((f, i) => (
                  <button
                    key={f.fileId || f.id || i}
                    onClick={() => onFileSelect?.(f)}
                    className={`p-2 rounded-lg transition-colors ${
                      selectedFileId === (f.fileId || f.id)
                        ? 'bg-amber-100 dark:bg-amber-900/30'
                        : 'hover:bg-slate-200 dark:hover:bg-neutral-700'
                    }`}
                    title={f.originalName || f.name}
                  >
                    <FileText
                      className={`h-4 w-4 ${
                        selectedFileId === (f.fileId || f.id) ? 'text-amber-600' : 'text-slate-500'
                      }`}
                    />
                  </button>
                ))}
              </>
            ) : (
              <>
                {notebooks.slice(0, 8).map(n => (
                  <button
                    key={n.id}
                    onClick={() => onSelect(n)}
                    className={`p-2 rounded-lg transition-colors ${
                      selectedNotebook?.id === n.id
                        ? 'bg-amber-100 dark:bg-amber-900/30'
                        : 'hover:bg-slate-200 dark:hover:bg-neutral-700'
                    }`}
                    title={n.title}
                  >
                    <FileText
                      className={`h-4 w-4 ${
                        selectedNotebook?.id === n.id ? 'text-amber-600' : 'text-slate-500'
                      }`}
                    />
                  </button>
                ))}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ✅ NEW: File type icon component with Microsoft Fluent UI icons from CDN
const FileTypeIcon = ({ extension, className = 'h-5 w-5' }) => {
  const [imageError, setImageError] = useState(false);
  const ext = (extension || '').toLowerCase();
  const tooltip = getFileTypeTooltip(ext);

  const VSCODE_ICONS_BASE =
    'https://raw.githubusercontent.com/vscode-icons/vscode-icons/master/icons';

  // VS Code Icons for Office documents
  const cdnIcons = {
    pdf: `${VSCODE_ICONS_BASE}/file_type_pdf.svg`,
    doc: `${VSCODE_ICONS_BASE}/file_type_word.svg`,
    docx: `${VSCODE_ICONS_BASE}/file_type_word.svg`,
    xls: `${VSCODE_ICONS_BASE}/file_type_excel.svg`,
    xlsx: `${VSCODE_ICONS_BASE}/file_type_excel.svg`,
    csv: `${VSCODE_ICONS_BASE}/file_type_excel.svg`,
    ppt: `${VSCODE_ICONS_BASE}/file_type_powerpoint.svg`,
    pptx: `${VSCODE_ICONS_BASE}/file_type_powerpoint.svg`,
  };

  const cdnIcon = cdnIcons[ext];

  // If we have a CDN icon and it hasn't failed, use it
  if (cdnIcon && !imageError) {
    return (
      <img
        src={cdnIcon}
        alt={`${ext} file`}
        title={tooltip}
        className={className}
        style={{ objectFit: 'contain', cursor: 'help' }}
        onError={() => setImageError(true)}
      />
    );
  }

  // Fallback to colored badges for non-CDN icons or if CDN fails
  // PDF - Red accent
  if (ext === 'pdf') {
    return (
      <div
        className={`${className} flex items-center justify-center rounded bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400`}
        title={tooltip}
        style={{ cursor: 'help' }}
      >
        <span className="text-[9px] font-bold">PDF</span>
      </div>
    );
  }

  // Word - Blue accent
  if (ext === 'doc' || ext === 'docx') {
    return (
      <div
        className={`${className} flex items-center justify-center rounded bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400`}
        title={tooltip}
        style={{ cursor: 'help' }}
      >
        <span className="text-[9px] font-bold">DOC</span>
      </div>
    );
  }

  // Excel - Green accent
  if (ext === 'xls' || ext === 'xlsx' || ext === 'csv') {
    return (
      <div
        className={`${className} flex items-center justify-center rounded bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400`}
        title={tooltip}
        style={{ cursor: 'help' }}
      >
        <span className="text-[9px] font-bold">XLS</span>
      </div>
    );
  }

  // PowerPoint - Orange accent
  if (ext === 'ppt' || ext === 'pptx') {
    return (
      <div
        className={`${className} flex items-center justify-center rounded bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400`}
        title={tooltip}
        style={{ cursor: 'help' }}
      >
        <span className="text-[9px] font-bold">PPT</span>
      </div>
    );
  }

  // Image files - Amber accent
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'tiff'].includes(ext)) {
    return (
      <div
        className={`${className} flex items-center justify-center rounded bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400`}
        title={tooltip}
        style={{ cursor: 'help' }}
      >
        <span className="text-[9px] font-bold">IMG</span>
      </div>
    );
  }

  // Text files - Gray accent
  if (ext === 'txt' || ext === 'md' || ext === 'rtf') {
    return (
      <div
        className={`${className} flex items-center justify-center rounded bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400`}
        title={tooltip}
        style={{ cursor: 'help' }}
      >
        <span className="text-[9px] font-bold">TXT</span>
      </div>
    );
  }

  // Default - Generic file
  return (
    <div
      className={`${className} flex items-center justify-center rounded bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400`}
      title={tooltip}
      style={{ cursor: 'help' }}
    >
      <FileText className="h-3 w-3" />
    </div>
  );
};

const SourceFileItem = ({
  file,
  vaultDetails,
  isSelected,
  onClick,
  sourceNumber,
  onRetry,
  onRemove,
  onDelete,
  onRename,
  onReupload,
  onReprocess,
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);

  // ✅ ENTERPRISE: Compute fileName/ext EARLY (used by multiple policy gates).
  // Fixes runtime TDZ: "Cannot access 'ext' before initialization"
  const fileName = file.originalName || file.name || file.filename || 'Document';
  const ext = fileName.split('.').pop()?.toLowerCase() || '';
  const mime = String(file?.mimeType || file?.mimetype || file?.mime_type || '').toLowerCase();
  const declaredType = String(file?._fileType || file?.fileType || '').toLowerCase();
  const isSpreadsheet =
    ['xls', 'xlsx', 'csv'].includes(ext) ||
    mime.includes('spreadsheet') ||
    mime.includes('excel') ||
    mime.includes('csv') ||
    ['excel', 'csv', 'spreadsheet'].includes(declaredType);
  const isOcrEligible =
    typeof file?._isOCREligible === 'boolean'
      ? file._isOCREligible
      : !isSpreadsheet &&
        (declaredType === 'pdf' ||
          declaredType === 'image' ||
          ['pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'tiff'].includes(ext) ||
          mime.startsWith('image/'));
  const retryActions = Array.isArray(file?._retryActions)
    ? file._retryActions.filter(action => {
        if (action?.action === 'reupload') return false;
        if (action?.action === 'retry_ocr' && !isOcrEligible) return false;
        return true;
      })
    : [];

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = e => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowMenu(false);
      }
    };
    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showMenu]);

  // ═══════════════════════════════════════════════════════════════════════════
  // ✅ ENTERPRISE V2: STRICTLY DETERMINISTIC STATUS (with backward compat)
  //
  // SINGLE SOURCE OF TRUTH (priority order):
  // 1. file.isReady === true (new records with explicit flag)
  // 2. status='ready' AND hasContent (legacy records without flag)
  // 3. ELSE → still processing
  // ═══════════════════════════════════════════════════════════════════════════
  const lifecycle = file.lifecycleSnapshot || null;
  const vaultState = String(vaultDetails?.state || '').toUpperCase();
  const vaultStatusOverride =
    vaultState === 'READY'
      ? 'ready'
      : vaultState === 'LOW_QUALITY'
        ? 'blocked_low_quality'
        : vaultState === 'ERROR'
          ? 'failed'
          : vaultState === 'UPLOADED'
            ? 'uploading'
            : ['EXTRACTING', 'OCR', 'EMBEDDING'].includes(vaultState)
              ? 'processing'
              : null;
  const rawStatus = String(
    vaultStatusOverride || lifecycle?.status || file.status || file.processingStatus || ''
  ).toLowerCase();
  const processingProgress =
    typeof lifecycle?.progress === 'number'
      ? lifecycle.progress
      : typeof file.processingProgress === 'number'
        ? file.processingProgress
        : null;

  // Content presence (for backward compat + warnings)
  const effectiveChunks = Number(vaultDetails?.effectiveChunkCount || 0) || 0;
  const qdrantCount = Number(vaultDetails?.qdrantCount || 0) || 0;
  const hasContent =
    effectiveChunks > 0 ||
    qdrantCount > 0 ||
    (file.chunkCount || 0) > 0 ||
    (file.embeddingCount || 0) > 0 ||
    (file.vectorCount || 0) > 0;

  // BACKWARD COMPAT: Accept both explicit isReady flag OR status='ready' with content
  const isReadyFlag =
    lifecycle?.isReady === true ||
    file.isReady === true ||
    ((rawStatus === 'ready' || rawStatus === 'ready_with_warnings') && hasContent);

  // ═══════════════════════════════════════════════════════════════════════════
  // ✅ SPEC: Canonical status derivation (legacy aliases mapped)
  // ═══════════════════════════════════════════════════════════════════════════
  let status;
  if (rawStatus === 'error' || rawStatus === 'failed') {
    status = 'failed';
  } else if (isReadyFlag) {
    status = 'ready'; // Ready if backend says isReady OR (status=ready + has content)
  } else if (rawStatus === 'ghost' || rawStatus === 'incomplete' || rawStatus === 'unavailable') {
    status = 'ghost';
  } else if (rawStatus === 'duplicate') {
    status = 'duplicate';
  } else if (rawStatus === 'blocked_low_quality' || rawStatus === 'blocked' || rawStatus === 'retry_ocr') {
    status = 'blocked_low_quality'; // legacy aliases → canonical
  } else if (rawStatus === 'ready_with_warnings') {
    status = 'ready_with_warnings'; // ✅ ENTERPRISE: Ready but with quality warnings
  } else if (rawStatus === 'restoring' || rawStatus === 'cache_restore') {
    status = 'restoring'; // legacy cache_restore → canonical restoring
  } else if (rawStatus === 'uploading') {
    status = 'uploading';
  } else if (rawStatus === 'queued') {
    status = 'queued';
  } else if (rawStatus === 'pending' || rawStatus === 'created' || rawStatus === 'new') {
    status = 'pending';
  } else {
    // Everything else is processing (queued, processing, analyzing, or unknown)
    status = 'processing';
  }

  const isReady = status === 'ready' || status === 'ready_with_warnings';
  const isReadyWithWarnings = status === 'ready_with_warnings';
  const isBlockedLowQuality = status === 'blocked_low_quality';
  const isRestoring = status === 'restoring';
  const isGhost = status === 'ghost' && !isReady;
  const isFailed = status === 'failed';
  const isIngestionFailed =
    isFailed && String(file?.processingStage || '').toLowerCase() === 'ingestion_failed';
  const isDuplicate = status === 'duplicate';
  const isUploading = status === 'uploading';
  const isProcessing = status === 'processing' || status === 'queued' || status === 'pending' || status === 'restoring';
  const isLoading = isUploading || isProcessing;
  const needsAction = isBlockedLowQuality || isGhost; // ✅ SPEC: Requires user intervention

  // ✅ FINAL UX CONTRACT: Authoritative user-facing trust state
  const trustState = String(file?.userTrust?.state || vaultDetails?.trustState || '').toLowerCase();
  const trustBadge = file?.userTrust?.badge || null;
  const trustEmoji = file?.userTrust?.emoji || null;
  const trustTooltip = file?.userTrust?.tooltip || null;

  // ✅ Victory moment: when a file becomes Readable/Trusted, make it feel like a win.
  const prevTrustRef = useRef(trustState);
  const [justUnlocked, setJustUnlocked] = useState(false);
  useEffect(() => {
    const prev = prevTrustRef.current;
    prevTrustRef.current = trustState;
    if (!prev || prev === trustState) return;
    const becameReliable =
      trustState === 'trusted'
        ? prev !== 'trusted'
        : trustState === 'readable'
          ? !['trusted', 'readable'].includes(prev)
          : false;
    if (!becameReliable) return;
    setJustUnlocked(true);
    const t = setTimeout(() => setJustUnlocked(false), 1200);
    t?.unref?.();
    return () => clearTimeout(t);
  }, [trustState]);

  // ═══════════════════════════════════════════════════════════════════════════
  // ✅ ENTERPRISE V2: Analysis State from PostIngestionAnalysisEngine
  // This is the SINGLE SOURCE OF TRUTH for AI analysis status.
  //
  // States: indexed → analysis_queued → analyzing → analyzed | skipped | halted
  // ═══════════════════════════════════════════════════════════════════════════
  const analysisState = String(
    file.userTrust?.analysisState || file.analysisState || ''
  ).toLowerCase();
  const isAnalyzed = analysisState === 'analyzed';
  const isAnalyzing = ['analysis_queued', 'analyzing'].includes(analysisState);
  const isAnalysisHalted = ['halted', 'skipped'].includes(analysisState);
  const isIndexedPendingAnalysis = analysisState === 'indexed' || !analysisState;

  // ✅ LEGACY: Keep insights status for backward compatibility during migration
  const insightsStatusRaw = String(file.insightsStatus || '').toLowerCase();
  const hasInsightsStatus = !!insightsStatusRaw;
  const understandingReady =
    isAnalyzed || ['ready', 'ready_with_warnings'].includes(insightsStatusRaw);
  const understandingBuilding = isAnalyzing || insightsStatusRaw === 'building';
  const understandingBlocked =
    isAnalysisHalted ||
    insightsStatusRaw === 'blocked' ||
    insightsStatusRaw === 'halted' ||
    insightsStatusRaw === 'blocked_low_quality';
  const understandingFailed = insightsStatusRaw === 'failed' || insightsStatusRaw === 'error';

  // ✅ ENTERPRISE V2: User-friendly analysis state hints
  // 🔒 PRODUCT LAW: No “AI analyzing…” narration.
  const analysisHint = (() => {
    if (isAnalyzed) return '✅ Analysis available';
    if (isAnalysisHalted) return '⏸️ Analysis paused';
    if (isIndexedPendingAnalysis) return null;
    return null;
  })();

  const understandingHint =
    analysisHint ||
    (hasInsightsStatus
      ? understandingBuilding
        ? '🧠 Understanding building'
        : understandingBlocked
          ? '🛑 Understanding blocked'
          : understandingFailed
            ? '❌ Understanding failed'
            : understandingReady
              ? '✅ Understanding ready'
              : `🧠 Understanding: ${insightsStatusRaw}`
      : null);

  // ═══════════════════════════════════════════════════════════════════════════════
  // ✅ ENTERPRISE: Trust-Based Display (replaces percentage-based quality)
  //
  // NEW MODEL: Trust is determined by SIGNALS from OCRTrustEvaluator, not scores.
  // The backend evaluates:
  // - structuralPlausibility: Is the text structure valid?
  // - linguisticSanity: Does it appear to be real language?
  // - documentExpectationMatch: Does content match document type expectations?
  // - semanticCoherence: Is the content meaningful?
  //
  // Trust states from backend (file.userTrust.state):
  // - 'trusted'     → VERIFIED / ACCEPTABLE (safe to rely on)
  // - 'readable'    → REVIEW_RECOMMENDED (usable but verify critical details)
  // - 'low_quality' → UNRELIABLE (needs retry or clearer upload)
  // - 'processing'  → Still being processed
  //
  // NO PERCENTAGE-BASED QUALITY IS SHOWN TO USERS.
  // ═══════════════════════════════════════════════════════════════════════════════

  // ✅ Index Coverage (for internal diagnostics only, not shown to users)
  const indexCoveragePct = (() => {
    if (typeof file.indexCoveragePct === 'number') return Math.round(file.indexCoveragePct);
    const chunks =
      Number(file.chunkCount || 0) || (Array.isArray(file.chunks) ? file.chunks.length : 0);
    const vectors = Number(file.embeddingCount || 0) || Number(file.vectorCount || 0) || 0;
    const indexOk = file.vectorIndexReady !== false;
    const queryOk = file.embeddingQueryReady !== false;
    let pct = 0;
    if (chunks > 0) pct += 35;
    if (vectors > 0) pct += 35;
    if (indexOk) pct += 20;
    if (queryOk) pct += 10;
    return pct > 0 ? Math.max(0, Math.min(100, pct)) : null;
  })();

  // ✅ DEPRECATED: Legacy quality score calculations (kept for backward compatibility)
  // These are NOT displayed to users. Trust state from backend is authoritative.
  const qualityScoreRaw = null; // Deprecated - use file.userTrust instead
  const qualityScore = 0; // Deprecated - no percentages shown
  const qualityTier = null; // Deprecated - use trustState
  const qualityColor = null; // Deprecated - use trustState

  // ✅ FINAL UX CONTRACT: No quality metrics shown in user UI
  const showQuality = false;
  const showIndexCoverage = false;

  // ✅ ENTERPRISE FIX: Robust file type detection (check extension, mimeType, and classification)
  const mimeType = file.mimeType || file.mime_type || '';
  const fileType = file.classification?.fileType || file.type || '';

  // ✅ ENTERPRISE: Check if metadata indicates Excel (has sheetCount/rowCount)
  const hasExcelMetadata = !!(
    file.sheetCount ||
    file.metadata?.sheets ||
    file.metadata?.sheet_count ||
    file.rowCount ||
    file.metadata?.rows ||
    file.metadata?.total_rows
  );

  const isExcel =
    ext === 'xlsx' ||
    ext === 'xls' ||
    ext === 'csv' ||
    mimeType.includes('spreadsheet') ||
    mimeType.includes('excel') ||
    fileType === 'spreadsheet' ||
    fileType === 'excel' ||
    hasExcelMetadata; // ✅ Fallback: if metadata has Excel fields, treat as Excel
  const isPdf = ext === 'pdf' || mimeType.includes('pdf') || fileType === 'pdf';
  const isWord =
    ext === 'docx' ||
    ext === 'doc' ||
    mimeType.includes('word') ||
    mimeType.includes('document') ||
    fileType === 'word' ||
    fileType === 'document';
  const isPowerPoint =
    ext === 'ppt' ||
    ext === 'pptx' ||
    mimeType.includes('powerpoint') ||
    mimeType.includes('presentation') ||
    fileType === 'powerpoint' ||
    fileType === 'presentation';

  // ✅ ENTERPRISE FIX: Correct metadata extraction based on file type
  // Excel files use sheetCount/rowCount, not pageCount
  // CRITICAL: For Excel, pages should always be 0 (Excel doesn't have pages)
  const pages = isExcel
    ? 0 // Excel doesn't have pages - always 0
    : Number(file.pages || file.pageCount || 0) ||
      (Array.isArray(file.pages) ? file.pages.length : 0);

  // ✅ ENTERPRISE: Extract Excel-specific metadata from multiple possible locations
  // Backend sends: file.sheetCount, file.rowCount, or file.metadata.sheets, file.metadata.rows
  const sheets = isExcel
    ? Number(file.sheetCount || file.metadata?.sheets || file.metadata?.sheet_count || 0)
    : 0;
  const rows = isExcel
    ? Number(file.rowCount || file.metadata?.rows || file.metadata?.total_rows || 0)
    : 0;

  const chunks =
    Number(file.chunkCount || 0) ||
    (Array.isArray(file.chunks) ? file.chunks.length : Number(file.chunks || 0));
  const vectors = Number(file.embeddingCount || 0) || Number(file.vectorCount || 0) || 0;
  const totalChunks = Number(file.totalChunks || file.expectedChunks || 0) || chunks;
  const progress =
    typeof lifecycle?.progress === 'number'
      ? lifecycle.progress
      : typeof file.progress === 'number'
        ? file.progress
        : typeof file.processingProgress === 'number'
          ? file.processingProgress
          : 0;
  const fromCache = file.fromFullCache || file.usedCache;

  // ✅ ENTERPRISE FIX: Human-friendly type label with correct priority
  // CRITICAL: Check Excel FIRST (before Word) to prevent misclassification
  // Excel files can sometimes have mimeType "document" which would match Word
  const typeLabel = isExcel
    ? 'Excel'
    : isPdf
      ? 'PDF'
      : isWord
        ? 'Word'
        : isPowerPoint
          ? 'PowerPoint'
          : ext
            ? ext.toUpperCase()
            : 'File';

  const sizeBytes = Number(file.size || file.fileSize || 0) || 0;
  const sizeText = sizeBytes > 0 ? formatBytes(sizeBytes, sizeBytes < 1024 * 1024 ? 0 : 1) : null;

  // ✅ FINAL UX CONTRACT: Do NOT use legacy `file.health` in user UI.
  // Health is an internal pipeline/index metric and caused contradictory user signals.
  const isProperlyProcessed =
    isReady &&
    !isLoading &&
    !isFailed &&
    !isDuplicate &&
    chunks > 0 &&
    vectors > 0 &&
    file.embeddingQueryReady !== false && // Query-embedding test passed
    file.vectorIndexReady !== false; // Vector store is durably indexed

  // ✅ ENTERPRISE: "Ready but not indexed" (causes empty answers)
  // This is a subset of "not properly processed" - specifically missing chunks/vectors
  const isUnindexed =
    isReady && !isLoading && !isFailed && !isDuplicate && (chunks === 0 || vectors === 0);

  // ✅ ENTERPRISE: "Ready but query-embedding broken" (chat won't work)
  const isQueryEmbeddingBroken =
    isReady &&
    !isLoading &&
    !isFailed &&
    !isDuplicate &&
    chunks > 0 &&
    vectors > 0 &&
    file.embeddingQueryReady === false;

  // ✅ ENTERPRISE: "Ready but vector index missing" (retrieval returns 0 even though metadata exists)
  const isVectorIndexBroken =
    isReady &&
    !isLoading &&
    !isFailed &&
    !isDuplicate &&
    chunks > 0 &&
    vectors > 0 &&
    file.vectorIndexReady === false;

  // ✅ ENTERPRISE: "Auto-Brief Halted" - Kill-switch triggered due to low OCR quality
  const isAutoBriefHalted = file.autoBriefHalted === true;
  const requiresManualIntervention = file.requiresManualIntervention === true;
  const autoBriefHaltedReason = file.autoBriefHaltedReason || 'LOW_QUALITY_SOURCE';
  const autoBriefMinQuality = file.autoBriefMinQuality || 80;
  const autoBriefActualQuality = file.autoBriefActualQuality || null; // internal only (do not show numbers to users)

  // ═══════════════════════════════════════════════════════════════════════════
  // ✅ ENTERPRISE V2: User-Friendly Status Text (NO jargon!)
  // - NO "chunks" or "vectors" shown to users
  // - Progress stages: Preparing → Analyzing → Extracting → Building → Ready
  // ═══════════════════════════════════════════════════════════════════════════

  // User-friendly processing stages based on progress and stage
  const getProcessingStageText = () => {
    const stage = String(lifecycle?.stage || file.processingStage || '').toLowerCase();
    const msg = typeof (lifecycle?.message || file.processingMessage) === 'string'
      ? String(lifecycle?.message || file.processingMessage).trim()
      : '';
    const progressPct =
      typeof processingProgress === 'number' && Number.isFinite(processingProgress)
        ? Math.max(0, Math.min(100, Math.round(processingProgress)))
        : null;
    const progressSuffix = progressPct !== null && progressPct > 0 && progressPct < 100
      ? ` · ${progressPct}%`
      : '';

    // ✅ ULTRA-RECOVER: Special states for deep scanning
    if (stage === 'fast_reocr') return '⚡ Re-OCR (fast)…';
    if (stage === 'deep_scanning') return '🔥 Deep Scanning...';
    if (stage === 'recovery_init') return '🔄 Initializing Recovery...';
    if (stage === 'purge') return '🗑️ Clearing old data...';
    if (stage === 'reset') return '↻ Resetting state...';
    if (stage === 'invalidate') return '🔓 Bypassing cache...';
    if (stage === 'cascade_init' || stage === 'cascade_engine') return '⚡ OCR Cascade...';
    if (stage === 'finalize') return '📊 Finalizing...';
    if (stage === 'regenerate') return '🧠 Refreshing AI...';
    if (stage === 'recovery_failed') return '❌ Recovery failed';
    // ✅ PHASE 4: Hash mismatch / cache repair transparency
    if (stage === 'reprocessing_cache_invalid') return msg || 'Reprocessing: cache repair';

    // Standard processing stages
    if (processingProgress === null || processingProgress < 15) return `Preparing document…${progressSuffix}`;
    if (processingProgress < 35) return `Analyzing structure…${progressSuffix}`;
    if (processingProgress < 65) return `Extracting text…${progressSuffix}`;
    if (processingProgress < 90) return `Building intelligence…${progressSuffix}`;
    return `Finalizing…${progressSuffix}`;
  };

  const statusText =
    isReady
      ? [
          typeLabel,
          sizeText,
          // Excel-specific: show sheets and rows instead of pages
          isExcel && sheets > 0 ? `${sheets} sheet${sheets === 1 ? '' : 's'}` : null,
          isExcel && rows > 0 ? `~${rows} row${rows === 1 ? '' : 's'}` : null,
          // PDF/Word/PowerPoint: show pages (with correct plurality)
          !isExcel && pages > 0 ? `${pages} ${pages === 1 ? 'page' : 'pages'}` : null,
          // Status indicators (user-friendly, not technical)
          isUnindexed ? '⚠️ Needs indexing' : null,
          isQueryEmbeddingBroken ? '⚠️ Chat unavailable' : null,
          isVectorIndexBroken ? '⚠️ Search unavailable' : null,
          // ✅ Trust-based warning (replaces quality score check)
          trustState === 'low_quality' ? '⚠️ Content needs attention' : null,
          // ✅ ENTERPRISE: Show understanding truth layer (prevents "processed vs analyzing" mismatch)
          trustState
            ? null
            : hasInsightsStatus && !understandingReady
              ? understandingHint
              : null,
          // ✅ KILL-SWITCH V2: Auto-Brief was halted due to low quality
          isAutoBriefHalted
            ? ['trusted', 'readable'].includes(trustState)
              ? 'Manual review required'
              : '🛑 Manual review required'
            : null,
        ]
          .filter(Boolean)
          .join(' · ')
      : trustState === 'processing' || trustState === 'reading'
        ? '🔄 Processing'
        : trustState === 'low_quality'
        ? '🔴 Low Quality'
        : isUploading
          ? 'Uploading...'
          : isProcessing
            ? getProcessingStageText()
            : isRestoring
              ? '⏳ Restoring from cache…'
              : isBlockedLowQuality
                ? `⛔ Blocked — ${file.retryOcrReason || file.stateReason || 'Text quality too low'}`
                : isReadyWithWarnings
                  ? '⚠️ Ready (low quality)'
                  : isGhost
                    ? '⚠️ Upload incomplete'
                    : isFailed
                    ? isIngestionFailed
                      ? 'Processing failed — please remove and re-upload.'
                      : String(file.error || '')
                          .toUpperCase()
                          .includes('LOW_OCR_QUALITY')
                        ? 'Action required: Re-run OCR'
                        : 'Failed'
                    : isDuplicate
                      ? 'Already exists'
                      : 'Ready';

  // ✅ PHASE 4: Restore transparency (cache provenance badge + tooltip)
  const restoredFromCache = file?.restoredFromCache === true;
  const restoredAt = file?.restoredAt || null;
  const restoredHash = file?.restoredHash || file?.fileHash || null;
  const restoredCacheScope = file?.restoredCacheScope || null;
  const restoredTenantId = file?.restoredTenantId || null;
  const cacheRestoreTooltip = restoredFromCache
    ? [
        'Restored from cache',
        restoredCacheScope ? `Scope: ${restoredCacheScope}` : null,
        restoredAt ? `At: ${restoredAt}` : null,
        restoredTenantId ? `Tenant: ${restoredTenantId}` : null,
        restoredHash ? `Hash: ${String(restoredHash).slice(0, 12)}…` : null,
      ]
        .filter(Boolean)
        .join('\n')
    : '';

  // ✅ PHASE 7: Universal error schema (file-level)
  const rawErr =
    file?.lastError ||
    file?.errorDetails ||
    (file?.error === true ? file : null) ||
    (file?.error && typeof file.error === 'object' ? file.error : null) ||
    (typeof file?.error === 'string' ? { error: true, code: 'FILE_ERROR', message: file.error } : null);

  const fileUiError = rawErr
    ? isUIUniversalError(rawErr)
      ? rawErr
      : normalizeUniversalError(rawErr, {
          source: 'sse',
          fallbackCode: 'FILE_ERROR',
          fallbackMessage: 'File processing failed',
          raw: rawErr,
        })
    : null;
  const showFileErrorBadge = Boolean(fileUiError && !isReady && !isReadyWithWarnings);

  const errorTooltip = (() => {
    if (!fileUiError) return '';
    try {
      return JSON.stringify(
        {
          error: true,
          code: fileUiError.code,
          message: fileUiError.message,
          details: fileUiError.details,
          timestamp: fileUiError.timestamp,
        },
        null,
        2
      );
    } catch {
      return `${fileUiError.code}: ${fileUiError.message}`;
    }
  })();

  // ✅ PHASE 7: Inline warnings (cache + restore governance)
  const restoreWarnings =
    file?.restoreWarnings ||
    file?.restore_warnings ||
    file?._restoreWarnings ||
    (Array.isArray(file?.warnings) ? file.warnings : null);
  const hasRestoreWarnings = Array.isArray(restoreWarnings) ? restoreWarnings.length > 0 : !!restoreWarnings;
  const partialRepair = file?.repaired === true || file?.partialRepair === true || file?._cacheRepaired === true;
  const certificateInvalid =
    file?._certificationBlocked === true ||
    String(file?._certificate?.status || '').toLowerCase() === 'invalid' ||
    String(file?.certificate?.status || '').toLowerCase() === 'invalid';
  const hashMismatchAutoReprocess =
    file?.hashMismatch === true ||
    String(file?.processingMessage || '').toLowerCase().includes('hash mismatch') ||
    String(file?.processingStage || '').toLowerCase().includes('hash_mismatch');

  return (
    <div
      onClick={!isLoading ? onClick : undefined}
      className={`group relative flex items-center gap-2.5 p-2.5 rounded-xl transition-all duration-200 ${
        isSelected
          ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 shadow-sm'
          : isLoading
            ? 'bg-slate-50 dark:bg-neutral-800/50 cursor-default'
            : 'hover:bg-slate-100 dark:hover:bg-neutral-700 text-slate-700 dark:text-neutral-300 cursor-pointer'
      } ${isFailed ? 'bg-red-50 dark:bg-red-900/10' : ''} ${isDuplicate ? 'bg-yellow-50 dark:bg-yellow-900/10' : ''} ${
        isGhost ? 'bg-amber-50 dark:bg-amber-900/20' : ''
      } ${justUnlocked
          ? trustState === 'trusted'
            ? 'ring-2 ring-green-300 dark:ring-green-700 animate-pulse'
            : 'ring-2 ring-yellow-200 dark:ring-yellow-700 animate-pulse'
          : ''
      }`}
    >
      {/* Progress bar for uploading/processing */}
      {isLoading && (
        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-slate-200 dark:bg-neutral-700 rounded-b-xl overflow-hidden">
          <div
            className={`h-full transition-all duration-500 ${
              String(file.processingStage || '').includes('deep_scanning') ||
              String(file.processingStage || '').includes('fast_reocr') ||
              String(file.processingStage || '').includes('cascade') ||
              String(file.processingStage || '').includes('recovery')
                ? 'bg-gradient-to-r from-red-500 via-orange-500 to-amber-500 animate-pulse' // Ultra-Recover mode
                : isUploading
                  ? 'bg-amber-500'
                  : 'bg-amber-500'
            }`}
            style={{ width: `${progress}%` }}
          />
        </div>
      )}

      {/* Source number badge */}
      <div
        className={`flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold transition-colors ${
          isSelected
            ? 'bg-amber-500 text-white'
            : isLoading
              ? 'bg-slate-200 dark:bg-neutral-600 text-slate-500 dark:text-neutral-400'
              : 'bg-slate-200 dark:bg-neutral-600 text-slate-600 dark:text-neutral-300'
        }`}
      >
        {isLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : sourceNumber}
      </div>

      {/* File info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          {/* ✅ Enhanced file type icon */}
          <FileTypeIcon extension={ext} className="h-5 w-5 flex-shrink-0" />
          <p
            dir="auto"
            className={`text-sm font-medium truncate ${
              isFailed
                ? 'text-red-600 dark:text-red-400'
                : isDuplicate
                  ? 'text-yellow-600 dark:text-yellow-400'
                  : isGhost
                    ? 'text-amber-700 dark:text-amber-300'
                  : ''
            }`}
            title={fileName}
          >
            {fileName}
          </p>
          {/* ✅ PHASE 7: Inline warning/error badges (cache/restore + universal error schema) */}
          {hasRestoreWarnings && (
            <span
              className="flex-shrink-0 text-[9px] font-bold px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200"
              title={
                Array.isArray(restoreWarnings)
                  ? ['Restore warnings:', ...restoreWarnings].join('\n')
                  : 'Restore warnings'
              }
              data-testid="istudio-file-restore-warnings"
            >
              WARN
            </span>
          )}
          {partialRepair && (
            <span
              className="flex-shrink-0 text-[9px] font-bold px-2 py-0.5 rounded-full bg-slate-200 dark:bg-neutral-700 text-slate-700 dark:text-neutral-200"
              title="Partial repair applied during cache restore"
              data-testid="istudio-file-partial-repair"
            >
              REPAIRED
            </span>
          )}
          {certificateInvalid && (
            <span
              className="flex-shrink-0 text-[9px] font-bold px-2 py-0.5 rounded-full bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300"
              title="Cache certificate invalid — reprocessing required"
              data-testid="istudio-file-certificate-invalid"
            >
              CERT
            </span>
          )}
          {hashMismatchAutoReprocess && (
            <span
              className="flex-shrink-0 text-[9px] font-bold px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200"
              title="Hash mismatch detected — auto reprocessing"
              data-testid="istudio-file-hash-mismatch"
            >
              HASH
            </span>
          )}
          {isGhost && (
            <span
              className="flex-shrink-0 text-[9px] font-bold px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200"
              title="Upload incomplete — file missing from analysis vault"
              data-testid="istudio-file-ghost"
            >
              GHOST
            </span>
          )}
          {restoredFromCache && (
            <span
              className="flex-shrink-0 text-[9px] font-bold px-2 py-0.5 rounded-full bg-slate-200 dark:bg-neutral-700 text-slate-700 dark:text-neutral-200"
              title={cacheRestoreTooltip}
            >
              CACHE
            </span>
          )}
          {showFileErrorBadge && (
            <span
              className="flex-shrink-0 text-[9px] font-bold px-2 py-0.5 rounded-full bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300"
              title={errorTooltip}
              data-testid="istudio-file-error-badge"
            >
              ERROR
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <p
            className={`text-[11px] ${
              isFailed
                ? 'text-red-500 dark:text-red-400'
                : isDuplicate
                  ? 'text-yellow-500 dark:text-yellow-400'
                  : isGhost
                    ? 'text-amber-600 dark:text-amber-300'
                  : isUnindexed
                    ? 'text-orange-600 dark:text-orange-400'
                    : isLoading
                      ? 'text-amber-600 dark:text-amber-400'
                      : 'text-slate-400 dark:text-neutral-500'
            }`}
          >
            {statusText}
          </p>
          {/* ✅ PHASE 7: Ingestion failure card (compact inline) */}
          {(isFailed || isBlockedLowQuality) && fileUiError && (
            <span
              className="ml-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300"
              title={errorTooltip}
              data-testid="istudio-file-error-pill"
            >
              {fileUiError.code}
            </span>
          )}
          {/* ═══════════════════════════════════════════════════════════════════════════
              FIX #5: BADGES FROM _badges ONLY (STRICT)
              UI MUST render ONLY _badges from Governor - NO local computation
          ═══════════════════════════════════════════════════════════════════════════ */}
          {file?._badges && file._badges.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {file._badges.map((badge, index) => {
                // Map badge color to Tailwind classes
                const colorClasses =
                  {
                    green: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
                    yellow:
                      'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200',
                    red: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
                    amber: 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200',
                    blue: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
                    slate: 'bg-slate-100 dark:bg-neutral-700 text-slate-700 dark:text-neutral-200',
                  }[badge.color] ||
                  'bg-slate-100 dark:bg-neutral-700 text-slate-700 dark:text-neutral-200';

                return (
                  <span
                    key={`${badge.type}-${index}`}
                    className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${colorClasses}`}
                    title={badge.tooltip || badge.label}
                  >
                    {badge.label}
                  </span>
                );
              })}
            </div>
          )}
          {/* Legacy fallback: Only show if _badges is not set (backward compat during migration) */}
          {(!file?._badges || file._badges.length === 0) && file?.userTrust && (
            <span
              className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                file.userTrust.state === 'trusted'
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                  : file.userTrust.state === 'readable'
                    ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200'
                    : file.userTrust.state === 'low_quality'
                      ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                      : file.userTrust.state === 'processing'
                        ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200'
                        : 'bg-slate-100 dark:bg-neutral-700 text-slate-700 dark:text-neutral-200'
              }`}
              title={file.userTrust.tooltip}
            >
              {file.userTrust.emoji} {file.userTrust.badge}
            </span>
          )}
        </div>
      </div>

      {/* Status icon and action buttons */}
      <div className="flex-shrink-0 flex items-center gap-1">
        {/* ⚡ Fast upload indicator */}
        {fromCache && isReady && (
          <div
            className="text-amber-500 dark:text-amber-400"
            title="⚡ Uploaded in less than 30 seconds!"
          >
            <Zap className="h-3.5 w-3.5" />
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════════════════
            FIX #6: RETRY BUTTONS FROM _retryActions ONLY (STRICT)
            UI MUST render ONLY _retryActions from Governor - NO local logic
        ═══════════════════════════════════════════════════════════════════════════ */}
        {retryActions.length > 0 && (
          <>
            {retryActions.map((action, index) => (
              <button
                key={`${action.action}-${index}`}
                onClick={e => {
                  e.stopPropagation();
                  if (action.action === 'retry_ocr') {
                    onReprocess(file, action.label, {
                      forceOcr: true,
                      tryCascade: true,
                      qualityThreshold: 85,
                    });
                  } else if (action.action === 'reupload') {
                    if (onReupload) {
                      onReupload(file);
                    } else {
                      console.warn('No onReupload handler provided');
                    }
                  } else if (action.action === 'reprocess') {
                    onReprocess(file, action.label, {});
                  }
                }}
                disabled={!action.enabled}
                className={`px-2 py-1 bg-gradient-to-r ${
                  action.action === 'retry_ocr'
                    ? 'from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600'
                    : action.action === 'reupload'
                      ? 'from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600'
                      : 'from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700'
                } rounded-lg transition-all flex items-center gap-1 text-[10px] font-bold text-white shadow-sm ${
                  !action.enabled ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                title={action.label}
              >
                <RotateCcw className="h-3 w-3" />
                {action.label}
              </button>
            ))}
          </>
        )}

        {/* ═══════════════════════════════════════════════════════════════════════════
            GOVERNOR BUTTONS ONLY - NO LEGACY FALLBACK
            UI MUST render ONLY _retryActions from Governor (Single Source of Truth)
            ═══════════════════════════════════════════════════════════════════════════ */}
        {retryActions.length > 0 && (
          <>
            {retryActions.map((action, index) => {
              // Handle indicator (non-clickable) - e.g., "Auto-fixing..."
              if (action.isIndicator) {
                return (
                  <span
                    key={`${action.action}-${index}`}
                    className="px-2 py-1 bg-gray-500 rounded-lg text-[10px] font-bold text-white opacity-70 flex items-center gap-1"
                    title={action.reason || action.label}
                  >
                    <Loader2 className="h-3 w-3 animate-spin" />
                    {action.label}
                  </span>
                );
              }

              // Clickable button
              const buttonClass =
                action.action === 'retry_ocr'
                  ? 'from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 animate-pulse'
                  : action.action === 'reupload'
                    ? 'from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600'
                    : 'from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700';

              return (
                <button
                  key={`${action.action}-${index}`}
                  onClick={e => {
                    e.stopPropagation();
                    if (action.action === 'retry_ocr') {
                      onReprocess(file, action.reason || 'Retry OCR', {
                        forceOcr: true,
                        tryCascade: true,
                        qualityThreshold: 85,
                      });
                    } else if (action.action === 'reupload') {
                      onReupload?.(file);
                    } else if (action.action === 'reprocess') {
                      onReprocess(file, action.reason || 'Reprocess', {});
                    }
                  }}
                  disabled={!action.enabled}
                  className={`px-2 py-1 bg-gradient-to-r ${buttonClass} rounded-lg transition-all flex items-center gap-1 text-[10px] font-bold text-white shadow-sm ${!action.enabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                  title={action.reason || action.label}
                >
                  {action.action === 'retry_ocr' || action.action === 'reprocess' ? (
                    <RotateCcw className="h-3 w-3" />
                  ) : (
                    <Upload className="h-3 w-3" />
                  )}
                  {action.label}
                </button>
              );
            })}
          </>
        )}

        {/* Removed: legacy +% health indicator (violated user trust contract) */}

        {/* ✅ KILL-SWITCH: Show when Auto-Brief was halted due to low quality
            ENTERPRISE V2: Only show if trust state is NOT trusted/readable (to avoid conflicting badges)
            When trust is positive, the file is queryable even though AI enrichment was halted.
        */}
        {isAutoBriefHalted && !['trusted', 'readable'].includes(trustState) && (
          <div
            className="px-2 py-0.5 bg-red-100 dark:bg-red-900/30 rounded-full text-[9px] font-bold text-red-700 dark:text-red-300 animate-pulse"
            title="Automatic analysis halted: this document needs review before a reliable understanding is possible."
          >
            🛑 Halted
          </div>
        )}

        {/* ✅ DEPRECATED: Percentage-based "Warning tier" CTA removed as part of OCR Trust Model cleanup
            The new trust-based system uses OCRTrustState (VERIFIED/ACCEPTABLE/REVIEW_RECOMMENDED/UNRELIABLE)
            instead of numeric quality percentages. Users now see trust badges, not % scores.
        */}

        {/* ✅ Error state: Show retry and remove buttons */}
        {isGhost ? (
          <div className="flex items-center gap-1">
            {onRemove && (
              <button
                onClick={e => {
                  e.stopPropagation();
                  onRemove(file);
                }}
                className="p-1 bg-amber-100 dark:bg-amber-900/30 rounded-full hover:bg-amber-200 dark:hover:bg-amber-800/50 transition-colors"
                title="Remove incomplete file"
              >
                <X className="h-3.5 w-3.5 text-amber-600 dark:text-amber-300" />
              </button>
            )}
            <div
              className="p-1 bg-amber-100 dark:bg-amber-900/30 rounded-full cursor-help"
              title="Upload incomplete — remove and re-upload to process"
            >
              <AlertCircle className="h-3.5 w-3.5 text-amber-600 dark:text-amber-300" />
            </div>
          </div>
        ) : isFailed ? (
          <div className="flex items-center gap-1">
            {/* Retry button */}
            {onRetry && (
              <button
                onClick={e => {
                  e.stopPropagation();
                  onRetry(file);
                }}
                className="p-1 bg-amber-100 dark:bg-amber-900/30 rounded-full hover:bg-amber-200 dark:hover:bg-amber-800/50 transition-colors"
                title={
                  file?.originalFile
                    ? 'Retry upload'
                    : 'Retry processing (uses server copy if available; no re-upload)'
                }
              >
                <RotateCcw className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
              </button>
            )}
            {/* Remove button */}
            {onRemove && (
              <button
                onClick={e => {
                  e.stopPropagation();
                  onRemove(file);
                }}
                className="p-1 bg-red-100 dark:bg-red-900/30 rounded-full hover:bg-red-200 dark:hover:bg-red-800/50 transition-colors"
                title="Remove failed file"
              >
                <X className="h-3.5 w-3.5 text-red-600 dark:text-red-400" />
              </button>
            )}
            {/* Error indicator with user-friendly message */}
            <div
              className="p-1 bg-red-100 dark:bg-red-900/30 rounded-full cursor-help"
              title={(() => {
                const errorMsg = file.error || 'Upload failed';
                if (String(errorMsg).toUpperCase().includes('LOW_OCR_QUALITY')) {
                  const score = file.qualityScore || file.ocrQuality?.score || null;
                  const warnAt = file.ocrQuality?.thresholds?.warning || 85;
                  return `⚠️ OCR quality too low${score !== null ? ` (${score}% < ${warnAt}%)` : ''}. Click “Retry OCR” (no re-upload). Safe outcome rules: better replaces cleanly, equal keeps best, worse is discarded. If still poor, delete the file and upload a clearer scan.`;
                }
                if (errorMsg.includes('DUPLICATE_FILE') || errorMsg.includes('already exists')) {
                  return '⚠️ This file already exists in the vault. Delete it first to upload again.';
                }
                if (
                  errorMsg.includes('Original file not found on disk') ||
                  errorMsg.includes('Re-upload required')
                ) {
                  return '⚠️ Source file missing: the vault no longer has the original copy. Delete it and upload a fresh copy if needed.';
                }
                if (errorMsg.includes('CACHE_CORRUPT') || errorMsg.includes('corrupt')) {
                  return '⚠️ File extraction failed. Try uploading again.';
                }
                if (errorMsg.includes('USER_DAILY_JOB_LIMIT')) {
                  return '⚠️ Daily processing limit reached for today. Try again tomorrow or contact an admin to increase your limit.';
                }
                if (errorMsg.includes('TENANT_DAILY_COST_LIMIT')) {
                  return '⚠️ Organization daily processing limit reached. Try again tomorrow or contact an admin.';
                }
                if (errorMsg.includes('binary data') || errorMsg.includes('encrypted')) {
                  return '⚠️ File could not be read. It may be encrypted or corrupted.';
                }
                if (errorMsg.includes('Network') || errorMsg.includes('timeout')) {
                  return '⚠️ Network error. Check your connection and retry.';
                }
                if (errorMsg.includes('too large') || errorMsg.includes('size')) {
                  return '⚠️ File is too large. Maximum size is 50MB.';
                }
                return `⚠️ Upload failed: ${errorMsg.length > 100 ? errorMsg.slice(0, 100) + '...' : errorMsg}`;
              })()}
            >
              <AlertCircle className="h-3.5 w-3.5 text-red-600 dark:text-red-400" />
            </div>
          </div>
        ) : isDuplicate ? (
          <div className="flex items-center gap-1">
            {onRemove && (
              <button
                onClick={e => {
                  e.stopPropagation();
                  onRemove(file);
                }}
                className="p-1 bg-yellow-100 dark:bg-yellow-900/30 rounded-full hover:bg-yellow-200 dark:hover:bg-yellow-800/50 transition-colors"
                title="Dismiss (file already exists in vault)"
              >
                <X className="h-3.5 w-3.5 text-yellow-600 dark:text-yellow-400" />
              </button>
            )}
            <div
              className="p-1 bg-yellow-100 dark:bg-yellow-900/30 rounded-full"
              title={file.error || 'This file already exists in this vault'}
            >
              <AlertCircle className="h-3.5 w-3.5 text-yellow-600 dark:text-yellow-400" />
            </div>
          </div>
        ) : trustState === 'trusted' ? (
          <div
            className="p-1 bg-green-100 dark:bg-green-900/30 rounded-full"
            title={trustTooltip || 'AI has read this document clearly. Safe to rely on.'}
          >
            <CheckCircle2 className="h-3.5 w-3.5 text-green-600 dark:text-green-400" />
          </div>
        ) : trustState === 'readable' ? (
          <div
            className="p-1 bg-yellow-100 dark:bg-yellow-900/30 rounded-full"
            title={trustTooltip || 'AI can read most content, but verify specific numbers/dates.'}
          >
            <AlertCircle className="h-3.5 w-3.5 text-yellow-700 dark:text-yellow-300" />
          </div>
        ) : trustState === 'low_quality' ? (
          <div
            className="p-1 bg-red-100 dark:bg-red-900/30 rounded-full"
            title={
              trustTooltip ||
              (isOcrEligible
                ? 'Scan is too blurry for accurate analysis. Please upload a clearer version.'
                : 'Content needs attention. Reprocess the file to restore usable extracted text.')
            }
          >
            <AlertCircle className="h-3.5 w-3.5 text-red-600 dark:text-red-400" />
          </div>
        ) : trustState === 'processing' || trustState === 'reading' ? (
          <div
            className="p-1 bg-amber-100 dark:bg-amber-900/30 rounded-full"
            title={trustTooltip || 'Processing'}
          >
            <Loader2 className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400 animate-spin" />
          </div>
        ) : isUploading ? (
          <div className="p-1 bg-amber-100 dark:bg-amber-900/30 rounded-full" title="Uploading...">
            <Upload className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400 animate-pulse" />
          </div>
        ) : isProcessing ? (
          <div className="p-1 bg-amber-100 dark:bg-amber-900/30 rounded-full" title="Processing…">
            <Loader2 className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400 animate-spin" />
          </div>
        ) : (
          <div className="p-1 bg-green-100 dark:bg-green-900/30 rounded-full">
            <CheckCircle2 className="h-3.5 w-3.5 text-green-600 dark:text-green-400" />
          </div>
        )}

        {/* ✅ Three-dots menu for file actions (always visible) */}
        {!isLoading && (
          <div className="relative" ref={menuRef}>
            <button
              onClick={e => {
                e.stopPropagation();
                setShowMenu(!showMenu);
              }}
              className="p-1 rounded-md hover:bg-slate-200 dark:hover:bg-neutral-600 transition-colors"
              title="More options"
            >
              <MoreVertical className="h-3.5 w-3.5 text-slate-400 dark:text-neutral-500" />
            </button>

            {/* Dropdown menu */}
            {showMenu && (
              <div className="absolute right-0 top-full mt-1 z-50 min-w-[140px] bg-white dark:bg-neutral-800 rounded-lg shadow-lg border border-slate-200 dark:border-neutral-700 py-1 animate-in fade-in slide-in-from-top-2 duration-150">
                {/* ✅ ENTERPRISE: Re-index option (when indexed data is missing) */}
                {isUnindexed && onReprocess && (
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      setShowMenu(false);
                      onReprocess(file, 'Re-index: missing search index');
                    }}
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-orange-700 dark:text-orange-300 hover:bg-orange-50 dark:hover:bg-orange-900/30 transition-colors"
                    title="Rebuild search index from the uploaded file (no re-upload needed)"
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                    Re-index
                  </button>
                )}

                {/* ⚡ Fast Re-OCR (single-engine, no cascade) */}
                {onReprocess && isOcrEligible && (
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      setShowMenu(false);
                      onReprocess(file, 'Re-OCR (fast path)', {
                        tryCascade: false,
                        forceEngine: 'mistral',
                        qualityThreshold: 70,
                      });
                    }}
                    disabled={isLoading}
                    className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs ${
                      isLoading
                        ? 'text-slate-400 dark:text-neutral-500 cursor-not-allowed'
                        : 'text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/30'
                    } transition-colors`}
                    title="Fast Re-OCR: single-engine pass using stored file (no re-upload, no cascade)"
                  >
                    <Zap className="h-3.5 w-3.5" />
                    Re-OCR (fast)
                  </button>
                )}

                {/* Rename option */}
                {onRename && (
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      setShowMenu(false);
                      onRename(file);
                    }}
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-slate-700 dark:text-neutral-300 hover:bg-slate-100 dark:hover:bg-neutral-700 transition-colors"
                  >
                    <Edit3 className="h-3.5 w-3.5" />
                    Rename
                  </button>
                )}

                {/* ✅ ENTERPRISE: Retry OCR (exclusive to Low Quality; menu discoverability) */}
                {!isFailed && trustState === 'low_quality' && onReprocess && isOcrEligible && (
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      setShowMenu(false);
                      onReprocess(file, 'Retry OCR (low quality)', {
                        forceOcr: true,
                        tryCascade: true,
                      });
                    }}
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
                    title="Retry OCR processing with better engines (no re-upload needed)"
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                    Retry OCR
                  </button>
                )}

                {/* Divider */}
                <div className="border-t border-slate-200 dark:border-neutral-700 my-1" />

                {/* Delete option */}
                {onDelete && (
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      setShowMenu(false);
                      onDelete(file);
                    }}
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Delete
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const NotebookListItem = ({ notebook, isSelected, onSelect, onDelete }) => (
  <div
    onClick={onSelect}
    className={`group flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
      isSelected
        ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
        : 'hover:bg-slate-100 dark:hover:bg-neutral-700 text-slate-700 dark:text-neutral-300'
    }`}
  >
    <FileText className="h-4 w-4 flex-shrink-0" />
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium truncate">{notebook.title}</p>
      <p className="text-xs text-slate-400 dark:text-neutral-500">
        {notebook.files?.length || notebook.fileCount || 0} sources
      </p>
    </div>
    {notebook.status === 'processing' && (
      <Loader2 className="h-3.5 w-3.5 animate-spin text-amber-600" />
    )}
    <button
      onClick={e => {
        e.stopPropagation();
        onDelete(notebook);
      }}
      className="hidden group-hover:block p-1 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 rounded"
      title="Delete notebook"
    >
      <Trash2 className="h-3 w-3" />
    </button>
  </div>
);

export default SourcesPanel;
